import * as XLSX from "xlsx";
import { LegData } from "./returns";
import logger from '../../logging/logger';

/**
 * Interface for option data rows in backtest results
 */
interface OptionRow {
  opt_type?: string;
  tradeDate?: string;
  symbol?: string;
  expirDate?: string;
  strike?: number;
  dte?: number;
  delta?: number;
  ask?: number;
  bid?: number;
  mid?: number;
  value?: number;
  mid_iv?: number;
  gamma?: number;
  vega?: number;
  theta?: number;
  rho?: number;
  contractID?: string;
  volume?: number;
  trade_start?: string;
  trade_end?: string;
  cost_to_trade?: number;
  direction?: string;
  quantity?: number;
  [key: string]: any;
}

/**
 * Column order for option data exports
 */
const OPTION_COLUMN_ORDER = [
  'tradeDate', 'symbol', 'expirDate', 'strike', 'dte', 'delta',
  'opt_type', 'ask', 'bid', 'mid', 'value', 'mid_iv', 'gamma',
  'vega', 'theta', 'rho', 'contractID', 'volume', 'trade_start', 'trade_end',
  'cost_to_trade', 'direction', 'quantity',
];

/**
 * Download backtest results as an Excel file with each leg in a separate sheet
 * @param backtestData - Raw backtest data with leg IDs as keys and trade rows as values
 */
export const downloadBacktestAsExcel = (backtestData: LegData): void => {
  if (!backtestData || Object.keys(backtestData).length === 0) {
    logger.error('No backtest data available for download');
    return;
  }

  logger.info("Downloading backtest data...");

  const workbook = XLSX.utils.book_new();

  // Create a sheet for each leg
  Object.keys(backtestData).forEach((legId, index) => {
    const legData = backtestData[legId];

    if (legData && legData.length > 0) {
      // Check if this is option data by looking for opt_type
      const isOptionData = legData[0] && legData[0].hasOwnProperty('opt_type');

      let worksheet: XLSX.WorkSheet;
      if (isOptionData) {
        // Reorder data according to specified column order
        const reorderedData = legData.map((row: OptionRow) => {
          const reorderedRow: Partial<OptionRow> = {};
          OPTION_COLUMN_ORDER.forEach(col => {
            if (row.hasOwnProperty(col)) {
              reorderedRow[col] = row[col];
            }
          });
          return reorderedRow;
        });

        worksheet = XLSX.utils.json_to_sheet(reorderedData, { header: OPTION_COLUMN_ORDER });
      } else {
        // For non-option data, use default ordering
        worksheet = XLSX.utils.json_to_sheet(legData);
      }

      const sheetName = `Leg ${index + 1}`;
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    }
  });

  // Generate filename with timestamp
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const filename = `backtest_results_${timestamp}.xlsx`;

  // Download the file
  XLSX.writeFile(workbook, filename);
  logger.info("Download complete!");
};

